import type { FC } from 'react'
import { useState } from 'react'

import { Popup } from '@skytech/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import CheckIcon from 'mdi-react/CheckIcon'
import CloseIcon from 'mdi-react/CloseIcon'
import DeleteIcon from 'mdi-react/DeleteIcon'
import WrenchIcon from 'mdi-react/WrenchIcon'

import injuryReportsResources from '@/api/injuryReports'
import queries from '@/api/queries'
import Button from '@/components/form/Button'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import { ApiCallerContent } from '@/components/util/ApiCaller'

import { INJURY_REPORT_STATUS_IDS } from '../constants'

import './InjuryReportOptions.styles.scss'

const InjuryReportOptions: FC<{
  injuryReportId: number
  statusId: number
  onSendSmsClick?: () => void
}> = ({ injuryReportId, statusId, onSendSmsClick }) => {
  const queryClient = useQueryClient()
  const translate = useTranslation()

  const [isApiCallModalOpen, setIsApiCallModalOpen] = useState(false)

  const {
    mutate: setStatus,
    error,
    isLoading: isSettingStatus,
    isSuccess,
    isError,
  } = useMutation(
    (statusId: number) => injuryReportsResources.setStatus({ statusId, injuryReportId }),
    {
      onMutate() {
        setIsApiCallModalOpen(true)
      },
      onSuccess() {
        queryClient.invalidateQueries(queries.injuryReport(injuryReportId))
        // todo: change when the rq updateOnMount is on again
        queryClient.invalidateQueries(queries.injuryReports(), { refetchType: 'all' })
        setIsApiCallModalOpen(false)
      },
      onError() {
        setIsApiCallModalOpen(true)
      },
    }
  )

  const isUnderRepairOption = [
    INJURY_REPORT_STATUS_IDS.FOR_CONSIDERATION,
    INJURY_REPORT_STATUS_IDS.ASSET_DISCARDED,
  ].includes(statusId)
  const isRepairOption = statusId === INJURY_REPORT_STATUS_IDS.ASSET_UNDER_REPAIR
  const isDiscardedOption = [
    INJURY_REPORT_STATUS_IDS.FOR_CONSIDERATION,
    INJURY_REPORT_STATUS_IDS.ASSET_UNDER_REPAIR,
    INJURY_REPORT_STATUS_IDS.ASSET_REPAIRED,
  ].includes(statusId)

  const isCloseOption = isDiscardedOption

  return (
    <div className="injury-report-options">
      {isUnderRepairOption && (
        <div className="injury-report-options__option">
          <Button
            Icon={WrenchIcon}
            disabled={isSettingStatus}
            onClick={() => setStatus(INJURY_REPORT_STATUS_IDS.ASSET_UNDER_REPAIR)}
          >
            <Translation id="Set to under repair" />
          </Button>
        </div>
      )}

      {onSendSmsClick && (
        <div className="injury-report-options__option">
          <Button disabled={isSettingStatus} onClick={onSendSmsClick}>
            <Translation id="Send sms" />
          </Button>
        </div>
      )}

      {isRepairOption && (
        <div className="injury-report-options__option">
          <Button
            Icon={CheckIcon}
            disabled={isSettingStatus}
            onClick={() => setStatus(INJURY_REPORT_STATUS_IDS.ASSET_REPAIRED)}
          >
            <Translation id="Set to repaired" />
          </Button>
        </div>
      )}

      {isDiscardedOption && (
        <div className="injury-report-options__option">
          <Button
            Icon={DeleteIcon}
            disabled={isSettingStatus}
            onClick={() => setStatus(INJURY_REPORT_STATUS_IDS.ASSET_DISCARDED)}
          >
            <Translation id="Set to discarded" />
          </Button>
        </div>
      )}

      {isCloseOption && (
        <div className="injury-report-options__option">
          <Button
            Icon={CloseIcon}
            disabled={isSettingStatus}
            onClick={() => setStatus(INJURY_REPORT_STATUS_IDS.CLOSED)}
          >
            <Translation id="Set to closed" />
          </Button>
        </div>
      )}

      <Popup
        ariaLabel={translate('Api call status')}
        isOpen={isApiCallModalOpen}
        onClose={() => setIsApiCallModalOpen(false)}
      >
        <div className="api-caller">
          <ApiCallerContent
            error={error}
            loadingText="Loading..."
            successText="API call completed successfully"
            errorText="API error"
            isLoading={isSettingStatus}
            isLoaded={isSuccess || isError}
          />
        </div>
      </Popup>
    </div>
  )
}

export default InjuryReportOptions
